
<script>
import axios from 'axios'
import authManager from '@/utils/auth'
import { isWechatBrowser, isMobileDevice, WX_LOGIN_MANUAL_KEY } from '@/utils/browser'
import {
  buildWechatQRLoginApiUrl,
  clearWechatSkipAutoLogin,
  isLoggedIn,
  shouldSkipWechatAutoLogin
} from '@/utils/loginFlow'
import {
  handleGuestClaimAfterAuth,
  readGuestClaimContext,
  stashGuestClaimContext
} from '@/utils/examClaim'

export default {
  name: 'LoginView',
  data() {
    return {
      username: '',
      password: '',
      captcha: '',
      captchaId: '',
      captchaImage: '',
      loading: false,
      wechatRedirecting: false,
      showManualLogin: false
    }
  },
  computed: {
    wechatLoginUrl() {
      const { href, sid, examId } = this.$route.query
      return buildWechatQRLoginApiUrl({
        origin: window.location.origin,
        href: typeof href === 'string' ? href : '',
        sid: typeof sid === 'string' ? sid : '',
        examId: examId != null ? String(examId) : ''
      })
    },
    isWechatBrowser2() {
      return isWechatBrowser()
    },
    isMobile() {
      return isMobileDevice()
    },
    hasPendingGuestClaim() {
      return !!(this.$route.query.sid && this.$route.query.examId)
    },
    registerLink() {
      return { path: '/register', query: { ...this.$route.query } }
    },
    wechatOfficialLoginUrl() {
      const ctx = readGuestClaimContext()
      const sid = this.$route.query.sid || ctx?.sid || ''
      const examId = this.$route.query.examId || ctx?.examId || ''
      const href = this.$route.query.href
      if (sid && examId) {
        const redirect = `${window.location.origin}/h5/exam/result`
        return (
          `/api/v1/wechat/official/base/login?sid=${encodeURIComponent(sid)}` +
          `&examId=${encodeURIComponent(String(examId))}` +
          `&redirect=${encodeURIComponent(redirect)}`
        )
      }
      if (href && typeof href === 'string') {
        return `/api/v1/wechat/official/login?href=${encodeURIComponent(href)}`
      }
      return '/api/v1/wechat/official/login'
    }
  },
  async created() {
    if (this.hasPendingGuestClaim) {
      stashGuestClaimContext({
        sid: this.$route.query.sid,
        examId: this.$route.query.examId
      })
    }
    if (!shouldSkipWechatAutoLogin(this.$route)) {
      sessionStorage.removeItem(WX_LOGIN_MANUAL_KEY)
    }
    await this.refreshCaptcha()
    this.tryAutoWechatLogin()
  },
  methods: {
    async refreshCaptcha() {
      try {
        const response = await axios.get('/api/v1/user/captcha')
        if (response.data && response.data.data) {
          this.captchaId = response.data.data.captchaId
          this.captchaImage = response.data.data.imageBase64
        }
      } catch (err) {
        console.error('Failed to get captcha:', err)
      }
    },
    focusNext(event, nextField) {
      if (nextField === 'loginBtn') {
        this.$refs.loginBtn.focus()
      } else {
        this.$refs[nextField + 'Input'].focus()
      }
    },
    async handleLogin() {
      if (!this.username || !this.password || !this.captcha) {
        this.$message.error('请填写完整信息')
        return
      }

      this.loading = true
      try {
        const response = await axios.post('/api/v1/user/login', {
          username: this.username,
          password: this.password,
          captcha: this.captcha,
          captchaId: this.captchaId,
        })

        if (response.data.code === 200 && response.data.data) {
          clearWechatSkipAutoLogin()
          const userInfo = await authManager.handleLoginSuccess(response.data.data)
          console.log("user/login ok ", userInfo)
          this.$root.login(response.data.data)

          try {
            const claimStatus = await handleGuestClaimAfterAuth(this.$router, this.$route)
            if (claimStatus === 'claimed' || claimStatus === 'recharge') {
              return
            }
          } catch (claimErr) {
            this.$message.error(claimErr.message || '生成报告失败')
            return
          }

          this.$message.success('登录成功')
          const href = this.$route.query.href
          if (href && typeof href === 'string') {
            this.$router.push(href)
          } else {
            this.$router.push('/')
          }
        } else if (response.data.code === 431) {
          this.$router.push({ path: '/security/captcha', query: { redirect: this.$route.fullPath } })
        } else {
          this.$message.error(response.data.message || '登录失败')
          
        }

      } catch (err) {
        console.error('Login failed:', err)
        this.$message.error(err.response?.data?.message || '登录失败')
        this.refreshCaptcha()
      } finally {
        this.loading = false
      }
    },
    onWechatClick() {
      if (this.hasPendingGuestClaim) {
        stashGuestClaimContext({
          sid: this.$route.query.sid,
          examId: this.$route.query.examId
        })
      }
    },
    tryAutoWechatLogin() {
      if (!isWechatBrowser() || isLoggedIn()) return
      if (shouldSkipWechatAutoLogin(this.$route)) {
        this.showManualLogin = true
        return
      }
      this.wechatRedirecting = true
      this.onWechatClick()
      window.location.href = this.wechatOfficialLoginUrl
    },
    onWechatOfficialClick() {
      clearWechatSkipAutoLogin()
      this.onWechatClick()
    }
  }
}
</script>

<template>
  <div class="login" :class="{ 'login--mobile': isMobile }">
    <div v-if="isWechatBrowser2 && wechatRedirecting && !showManualLogin" class="wechat-auto-redirect">
      <div class="wechat-auto-redirect__spinner" />
      <p>正在跳转微信登录…</p>
    </div>

    <template v-else>
    <h2 v-if="!isMobile && !(isWechatBrowser2 && !showManualLogin)">登录</h2>

    <el-alert
      v-if="hasPendingGuestClaim"
      type="info"
      :closable="false"
      show-icon
      class="guest-alert"
      title="您已完成测评"
      :description="isWechatBrowser2
        ? '将使用微信一键登录为您生成测评报告。也可改用账号密码登录。'
        : '登录或注册后，将自动为您生成测评报告。没有账号可使用邮箱注册，或使用微信扫码登录。'"
    />
    <form v-if="!isWechatBrowser2 || showManualLogin" @submit.prevent="handleLogin">
      <div>
        <label for="username">用户名</label>
        <input
          type="text"
          id="username"
          v-model="username"
          required
          autocomplete="username"
          @keydown.tab.prevent="focusNext($event, 'password')"
          @keydown.enter.prevent="focusNext($event, 'password')"
          ref="usernameInput"
        >
      </div>
      <div>
        <label for="password">密码</label>
        <input
          type="password"
          id="password"
          v-model="password"
          required
          autocomplete="current-password"
          @keydown.tab.prevent="focusNext($event, 'captcha')"
          @keydown.enter.prevent="focusNext($event, 'captcha')"
          ref="passwordInput"
        >
      </div>
      <div class="captcha-container">
        <label for="captcha">图形验证码</label>
        <div class="captcha-input-group">
          <input 
            type="text" 
            id="captcha" 
            v-model="captcha" 
            required
            @keydown.tab.prevent="focusNext($event, 'loginBtn')"
            @keydown.enter.prevent="handleLogin"
            ref="captchaInput"
          >
          <img 
            :src="captchaImage" 
            alt="图形验证码"
            class="captcha-image"
            @click="refreshCaptcha"
          >
        </div>
      </div>
      <button
        type="submit"
        ref="loginBtn"
        :disabled="loading"
        @keydown.tab.prevent="focusNext($event, 'username')"
      >
        {{ loading ? '登录中…' : '登录' }}
      </button>
    </form>

    <p v-if="!isMobile || (isWechatBrowser2 && showManualLogin)" class="register-hint">
      没有账号？
      <router-link :to="registerLink">邮箱注册</router-link>
    </p>

    <div v-if="!isMobile || isWechatBrowser2" class="social-login" :class="{ 'social-login--mobile': isMobile }">
      <h3 v-if="!isMobile && (!isWechatBrowser2 || showManualLogin)">或使用微信登录</h3>

      <a
        v-if="!isWechatBrowser2 && !isMobile"
        :href="wechatLoginUrl"
        class="wechat-btn"
        @click="onWechatClick"
      >
        <img src="https://res.wx.qq.com/a/wx_fed/assets/res/OTE0YTAw.png" alt="微信" class="wechat-icon">
        微信扫码登录
      </a>
      <template v-if="isWechatBrowser2 && showManualLogin">
        <a
          :href="wechatOfficialLoginUrl"
          class="wechat-btn wechat-btn--block"
          @click="onWechatOfficialClick"
        >
          <img src="https://res.wx.qq.com/a/wx_fed/assets/res/OTE0YTAw.png" alt="微信" class="wechat-icon">
          微信一键登录
        </a>
        <p class="wechat-manual-hint">
          <router-link :to="registerLink">邮箱注册</router-link>
        </p>
      </template>
    </div>
    <p v-else-if="isMobile" class="mobile-wechat-hint">
      <router-link :to="registerLink">邮箱注册</router-link>
    </p>
    </template>
  </div>
</template>


<style scoped>
.login {
  max-width: 400px;
  margin: 0 auto;
  padding: 20px;
}

.guest-alert {
  margin-bottom: 20px;
}

.register-hint {
  margin-top: 16px;
  text-align: center;
  color: #666;
}

form div {
  margin-bottom: 15px;
}

label {
  display: block;
  margin-bottom: 5px;
}

input {
  width: 100%;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
}

.captcha-container {
  margin-bottom: 20px;
}

.captcha-input-group {
  display: flex;
  gap: 10px;
  align-items: center;
}

.captcha-image {
  height: 40px;
  cursor: pointer;
  border: 1px solid #ddd;
  border-radius: 4px;
}

button {
  padding: 10px 20px;
  background-color: #42b983;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  margin-top: 10px;
  width: 100%;
}

button:hover {
  background-color: #3aa876;
}

button:disabled {
  background-color: #cccccc;
  cursor: not-allowed;
}

.social-login {
  margin-top: 30px;
  text-align: center;
}

.social-login h3 {
  color: #666;
  margin-bottom: 15px;
  position: relative;
}

.social-login h3:before,
.social-login h3:after {
  content: "";
  position: absolute;
  top: 50%;
  width: 30%;
  height: 1px;
  background-color: #ddd;
}

.social-login h3:before {
  left: 0;
}

.social-login h3:after {
  right: 0;
}

.wechat-btn {
  display: inline-flex;
  align-items: center;
  padding: 10px 20px;
  background-color: #07C160;
  color: white;
  text-decoration: none;
  border-radius: 4px;
  transition: background-color 0.3s;
}

.wechat-btn:hover {
  background-color: #06ad56;
}

.wechat-icon {
  width: 24px;
  height: 24px;
  margin-right: 10px;
}

.login--mobile {
  max-width: none;
  margin: 0;
  padding: 16px;
  min-height: calc(100vh - 56px);
  box-sizing: border-box;
  background: #fff;
  border-radius: 12px 12px 0 0;
}

.login--mobile form {
  margin-top: 8px;
}

.social-login--mobile {
  margin-top: 20px;
}

.wechat-btn--block {
  display: flex;
  width: 100%;
  justify-content: center;
  box-sizing: border-box;
}

.mobile-wechat-hint {
  margin-top: 20px;
  text-align: center;
  font-size: 14px;
  color: #666;
}

.mobile-wechat-hint .dot {
  margin: 0 8px;
  color: #ccc;
}

.link-wechat {
  color: #07c160;
  text-decoration: none;
}

.wechat-auto-redirect {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 240px;
  color: #606266;
  gap: 16px;
}

.wechat-auto-redirect__spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #e4e7ed;
  border-top-color: #07c160;
  border-radius: 50%;
  animation: login-spin 0.8s linear infinite;
}

@keyframes login-spin {
  to { transform: rotate(360deg); }
}

.wechat-manual-hint {
  margin-top: 12px;
  text-align: center;
  font-size: 14px;
}
</style> 